import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import env from '../config/env';
import { useMarket } from './MarketContext';
import { getActiveOffers } from '../api/real/offersApi';
import { offerCoversProduct } from '../utils/offerMatch';

/** The store's automatic offers, loaded once per visit and market — for badges, the product page and banners. */
const OffersContext = createContext({ offers: [], offersFor: () => [], sym: '₹' });
const REFRESH_MS = 5 * 60 * 1000;

export const OffersProvider = ({ children }) => {
  const { currentMarket } = useMarket();
  const marketCode = currentMarket?.isDefault ? null : (currentMarket?.code ?? null);
  const [offers, setOffers] = useState([]);

  useEffect(() => {
    if (env.isMock) return undefined;
    let alive = true;
    const load = () => getActiveOffers(marketCode)
      .then((list) => { if (alive) setOffers(list); })
      .catch(() => { /* keep what is shown */ });
    load();
    // a shopper who keeps the site open sees offers that start or end: re-check on return to the tab and every 5 min
    const onVisible = () => { if (document.visibilityState === 'visible') load(); };
    document.addEventListener('visibilitychange', onVisible);
    const timer = setInterval(load, REFRESH_MS);
    return () => { alive = false; document.removeEventListener('visibilitychange', onVisible); clearInterval(timer); };
  }, [marketCode]);

  const offersFor = useCallback((product) => offers.filter((o) => offerCoversProduct(o, product)), [offers]);
  const sym = currentMarket?.currencySymbol ?? '₹';   // offer prices come in the market's currency
  const value = useMemo(() => ({ offers, offersFor, sym }), [offers, offersFor, sym]);
  return <OffersContext.Provider value={value}>{children}</OffersContext.Provider>;
};

export const useOffers = () => useContext(OffersContext);
